import React, { useState } from 'react';
import { Box, Checkbox, Collapse, Grid, IconButton, Typography } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { ExpandMore, ExpandLess } from '@material-ui/icons';
import { FieldIcon } from '../../../admin/admin-console/admin-console-content-pages/admin-console-volunteer-profile/form-builder/dialogs/field-task-item-dialog/FieldIcon';

interface Task {
  taskId: string;
  title: string;
  description?: string;
  cake: {
    cakeId: string;
    title: string;
    cakeType: string;
  };
  adminOnly: boolean;
  isEventTask: boolean;
  isRefereeCheckTask: boolean;
  managerCompleted: boolean;
  taskItems: Array<{
    __typename: string;
    taskItemId: string;
    dateRemoved: string | null;
    field: {
      __typename: string;
      taskFieldId: string;
      name: string;
      dateDeactivated: string | null;
    };
  }>;
}

export interface TaskFieldColumn {
  fieldId: string;
  fieldName: string;
  fieldType: string;
  taskId: string;
  taskTitle: string;
}

interface TaskFieldSelectorProps {
  tasks: Task[];
  selectedFields: Map<string, TaskFieldColumn>;
  onToggleField: (task: Task, field: any) => void;
  onToggleAllTaskFields: (task: Task, fields: any[]) => void;
}

const useStyles = makeStyles((theme) =>
  createStyles({
    taskBox: {
      backgroundColor: '#f9fafb',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '16px',
      transition: 'all 0.2s ease',
    },
    taskHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      cursor: 'pointer',
    },
    taskHeaderLeft: {
      display: 'flex',
      gap: '8px',
      flex: 1,
      alignItems: 'flex-start',
    },
    cakeTitle: {
      fontSize: '10px',
      fontWeight: 500,
      color: '#6b7280',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      marginBottom: '2px',
    },
    taskTitle: {
      fontSize: '15px',
      fontWeight: 700,
      color: '#111827',
      marginBottom: '6px',
      transition: 'color 0.2s ease',
    },
    badgeContainer: {
      display: 'flex',
      gap: '6px',
      flexWrap: 'wrap',
    },
    badge: {
      fontSize: '0.75rem',
      fontWeight: 550,
      padding: 8,
      paddingLeft: 12,
      paddingRight: 12,
      borderRadius: 4,
      backgroundColor: '#D9D9D9',
      color: theme.color.grey.neutral400,
    },
    fieldContainer: {
      marginTop: '12px',
      paddingTop: '12px',
      borderTop: '1px solid #e5e7eb',
    },
    fieldBox: {
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '4px',
      padding: '8px 8px 6px 8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '6px',
      minHeight: '40px',
      position: 'relative',
      '&.selected': {
        borderColor: '#7047EB',
        backgroundColor: '#f5f3ff',
      },
    },
    fieldType: {
      fontSize: '9px',
      fontWeight: 500,
      color: '#6b7280',
      marginBottom: '2px',
      display: 'flex',
      alignItems: 'center',
      gap: '3px',
    },
    fieldName: {
      fontSize: '13px',
      fontWeight: 400,
      color: '#4b5563',
      lineHeight: '1.4',
    },
  })
);

const getFieldTypeLabel = (typename: string): string => {
  const typeMap: Record<string, string> = {
    VOLUNTEER_AddressTaskFieldType: 'Address',
    VOLUNTEER_ShortTextTaskFieldType: 'Short Text',
    VOLUNTEER_LongTextTaskFieldType: 'Long Text',
    VOLUNTEER_NumberTaskFieldType: 'Number',
    VOLUNTEER_DateTaskFieldType: 'Date',
    VOLUNTEER_EmailTaskFieldType: 'Email',
    VOLUNTEER_PhoneTaskFieldType: 'Phone',
    VOLUNTEER_DropdownTaskFieldType: 'Dropdown',
    VOLUNTEER_MultipleChoiceTaskFieldType: 'Multiple Choice',
    VOLUNTEER_CheckboxTaskFieldType: 'Checkbox',
    VOLUNTEER_QuizTaskFieldType: 'Quiz',
    VOLUNTEER_AttachmentTaskFieldType: 'Attachment',
    VOLUNTEER_SignatureTaskFieldType: 'Signature',
  };
  return typeMap[typename] || typename;
};

const getTaskTypeBadge = (task: Task, classes: any) => {
  if (task.isRefereeCheckTask) {
    return (
      <Box className={classes.badge} style={{ backgroundColor: '#f3e5f5', color: '#7b1fa2' }}>
        Referee Check
      </Box>
    );
  }

  if (task.isEventTask) {
    return (
      <Box className={classes.badge} style={{ backgroundColor: '#e0f2f1', color: '#00695c' }}>
        Event Task
      </Box>
    );
  }

  if (task.managerCompleted) {
    return (
      <Box className={classes.badge} style={{ backgroundColor: '#FFF4E6', color: '#E65100' }}>
        Manager Completes
      </Box>
    );
  }

  return (
    <Box className={classes.badge} style={{ backgroundColor: '#E3F2FD', color: '#1565C0' }}>
      User Completes
    </Box>
  );
};

export const TaskFieldSelector = (props: TaskFieldSelectorProps) => {
  const { tasks, selectedFields, onToggleField, onToggleAllTaskFields } = props;
  const classes = useStyles();
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());

  const handleToggleExpand = (taskId: string) => {
    setExpandedTasks((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  const getTaskCheckboxState = (fields: any[]) => {
    const selectedCount = fields.filter((field) => selectedFields.has(field.taskFieldId)).length;

    if (selectedCount === 0) {
      return { checked: false, indeterminate: false };
    }

    if (selectedCount === fields.length) {
      return { checked: true, indeterminate: false };
    }

    return { checked: false, indeterminate: true };
  };

  return (
    <Box>
      {tasks.map((task: Task) => {
        const fields =
          task.taskItems
            ?.filter((item: any) => item?.field && !item.dateRemoved && !item.field.dateDeactivated)
            .map((item: any) => item.field) || [];

        if (fields.length === 0) return null;

        const isExpanded = expandedTasks.has(task.taskId);
        const { checked, indeterminate } = getTaskCheckboxState(fields);

        return (
          <Box key={task.taskId} className={classes.taskBox}>
            {/* Task header */}
            <Box className={classes.taskHeader}>
              <Box className={classes.taskHeaderLeft}>
                <Checkbox
                  checked={checked}
                  indeterminate={indeterminate}
                  size='small'
                  style={{
                    padding: 0,
                    marginTop: '2px',
                    color: checked || indeterminate ? '#7047EB' : undefined,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleAllTaskFields(task, fields);
                  }}
                />
                <Box flex={1} onClick={() => handleToggleExpand(task.taskId)}>
                  <Typography className={classes.cakeTitle}>{task.cake.title}</Typography>
                  <Typography className={classes.taskTitle}>{task.title}</Typography>
                  <Box className={classes.badgeContainer}>{getTaskTypeBadge(task, classes)}</Box>
                </Box>
              </Box>
              <IconButton size='small' style={{ marginTop: '-4px' }} onClick={() => handleToggleExpand(task.taskId)}>
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>

            {/* Field selection area - collapsible */}
            <Collapse in={isExpanded}>
              <Box className={classes.fieldContainer}>
                <Grid container spacing={1}>
                  {fields.map((field: any) => {
                    const isSelected = selectedFields.has(field.taskFieldId);

                    return (
                      <Grid item xs={6} key={field.taskFieldId}>
                        <Box
                          className={`${classes.fieldBox} ${isSelected ? 'selected' : ''}`}
                          onClick={() => {
                            onToggleField(task, field);
                          }}
                        >
                          <Checkbox
                            checked={isSelected}
                            size='small'
                            style={{
                              padding: 0,
                              color: isSelected ? '#7047EB' : undefined,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onToggleField(task, field);
                            }}
                          />
                          <Box flex={1}>
                            <Box className={classes.fieldType}>
                              <FieldIcon fieldType={field.__typename as any} style={{ fontSize: '10px' }} />
                              {getFieldTypeLabel(field.__typename)}
                            </Box>
                            <Typography className={classes.fieldName}>{field.name}</Typography>
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
};
